import { tz } from '@date-fns/tz';
import { addMinutes, isBefore, parseISO } from 'date-fns';
import { filter, groupBy, uniq } from 'lodash-es';
import type { AvailabilityState } from '@shared/availability/availabilityReducer';
import { toISODateFormat } from '@shared/utils/dateFormatters';
import { type TSelectedAvailability } from 'webReservations/context/AvailabilityContext';
import type { Availability } from 'webReservations/restaurant/apiHelpers';
import { createReferenceDate } from '@shared/utils/createReferenceDate';

const filterAvailabilitiesByGuestCount = (
  availabilities: Availability[],
  guestCount: number,
): Availability[] =>
  filter(
    availabilities,
    (item: Availability) => guestCount === item.guestCount,
  );

const filterAvailabilitiesByTime = (
  availabilities: Availability[],
  selectedTime: string | null,
): Availability[] => {
  if (!selectedTime) {
    return availabilities;
  }

  return filter(
    availabilities,
    (item: Availability) => selectedTime === item.time,
  );
};

const filterAvailabilitiesBySelectedFloorPlanId = (
  availabilities: Availability[],
  selectedFloorPlanId: string,
): Availability[] =>
  filter(
    availabilities,
    (item: Availability) => selectedFloorPlanId === item.listing.floorPlanId,
  );

const filterAvailabilitiesBySelectedFloorPlanListingIds = (
  availabilities: Availability[],
  selectedFloorPlanListingIds: string[],
): Availability[] => {
  if (!selectedFloorPlanListingIds.length) {
    return availabilities;
  }

  return filter(availabilities, (item: Availability) =>
    selectedFloorPlanListingIds.includes(item.listing.id),
  );
};

/**
 * Sorts availabilities by `time` then `purchasePrice`, in ascending order.
 *
 * Note: offer-only availabilities (`!isBuyable`) come before availabilities
 * with a purchasePrice.
 */
const timeAndPriceSorter = (a: Availability, b: Availability) => {
  const aDate = createReferenceDate(a.time, new Date(a.date));
  const bDate = createReferenceDate(b.time, new Date(b.date));
  const timeComparison = aDate.getTime() - bDate.getTime();
  if (timeComparison !== 0) {
    return timeComparison;
  }

  if (!a.isBuyable && !b.isBuyable) {
    return 0;
  }
  if (!a.isBuyable || a.purchasePrice === null) {
    return -1;
  }
  if (!b.isBuyable || b.purchasePrice === null) {
    return 1;
  }
  return a.purchasePrice - b.purchasePrice;
};

const isTrulyBiddable =
  (selectedDate: Date, timezone: string) => (availability: Availability) => {
    const thirtyMinutesFromNow = addMinutes(new Date(), 30);
    const availabilityStartTime = parseISO(
      `${toISODateFormat(selectedDate)}T${availability.time}`,
      { in: tz(timezone) },
    );

    return {
      ...availability,
      isBiddable:
        availability.isBiddable &&
        isBefore(thirtyMinutesFromNow, availabilityStartTime),
    };
  };

const isTrulyBiddableOrBuyable =
  (selectedDate: Date, timezone: string) => (availability: Availability) => {
    const thirtyMinutesFromNow = addMinutes(new Date(), 30);
    const availabilityStartTime = parseISO(
      `${toISODateFormat(selectedDate)}T${availability.time}`,
      { in: tz(timezone) },
    );

    return (
      (availability.isBiddable &&
        isBefore(thirtyMinutesFromNow, availabilityStartTime)) ||
      availability.isBuyable
    );
  };

export const getAvailabilitiesGroupedByTime = (
  state: AvailabilityState<Availability, TSelectedAvailability>,
  timezone: string,
): Record<string, Availability[]> =>
  getAvailabilitiesGroupedByTimeInternal(state, timezone, {
    filterBySelectedListings: true,
  });

export const getAvailabilitiesGroupedByTimeForTimePicker = (
  state: AvailabilityState<Availability, TSelectedAvailability>,
  timezone: string,
): Record<string, Availability[]> =>
  getAvailabilitiesGroupedByTimeInternal(state, timezone, {
    filterBySelectedListings: false,
  });

const getAvailabilitiesGroupedByTimeInternal = (
  state: AvailabilityState<Availability, TSelectedAvailability>,
  timezone: string,
  { filterBySelectedListings }: { filterBySelectedListings: boolean },
): Record<string, Availability[]> => {
  const filterFns = [
    (availabilities: Availability[]) =>
      filterAvailabilitiesByGuestCount(
        availabilities,
        state.selectedGuestCount,
      ),
    (availabilities: Availability[]) =>
      filterAvailabilitiesBySelectedFloorPlanId(
        availabilities,
        state.selectedFloorPlanId,
      ),
    ...(filterBySelectedListings
      ? [
          (availabilities: Availability[]) =>
            filterAvailabilitiesBySelectedFloorPlanListingIds(
              availabilities,
              state.selectedFloorPlanListingIds,
            ),
        ]
      : []),
  ];

  const filteredSortedAvailabilities = filterFns
    .reduce(
      (filteredData, filterFn) => filterFn(filteredData),
      state.availabilities,
    )
    .filter(isTrulyBiddableOrBuyable(state.selectedDate, timezone))
    .sort(timeAndPriceSorter);

  return groupBy(filteredSortedAvailabilities, 'time');
};

export const findBestAvailableForTime = (
  availabilities: Availability[],
  time: string,
): Availability | undefined => {
  const bestAvailableForTime = availabilities.filter(
    (availability) =>
      availability.time === time && availability.isBestAvailable,
  );

  // Prefer a listing the guest can actually reserve outright. A time can have
  // more than one "best available" flagged (one per floor plan), and an
  // offer-only one (biddable, not buyable, null price) sorting ahead of a
  // purchasable one would silently auto-select a bid the guest never chose to
  // make, leaving checkout stuck on a $0 display it can't actually complete.
  // Fall back to whichever is flagged when every option for this time is
  // offer-only, so that genuine offer slots still route into the offer flow.
  return (
    bestAvailableForTime.find(
      (availability) =>
        availability.isBuyable && availability.purchasePrice !== null,
    ) ?? bestAvailableForTime[0]
  );
};

export const toSelectedAvailability = (
  availability: Availability,
): TSelectedAvailability => ({
  isOffer: availability.isBiddable && !availability.isBuyable,
  isResale: availability.isResale,
  isBestAvailable: availability.isBestAvailable,
  purchasePrice: availability.purchasePrice,
  standardPrice: availability.standardPrice ?? null,
  listingId: availability.listing.id,
  publicName: availability.listing.publicName ?? '',
  time: availability.time,
  date: availability.date,
});

// TODO: Refactor this function to be more readable and cohesive/single responsibility
export const calculateFilteredData = (
  state: AvailabilityState<Availability, TSelectedAvailability>,
  timezone: string,
) => {
  const { availabilities, selectedFloorPlanListingIds } = state;

  const availabilitiesFilteredByGuestCount = filterAvailabilitiesByGuestCount(
    state.availabilities,
    state.selectedGuestCount,
  );

  const availabilitiesFilteredByGuestCountAndTime = filterAvailabilitiesByTime(
    availabilitiesFilteredByGuestCount,
    state.selectedTime,
  );

  const availabilitiesFilteredByGuestCountAndTimeAndFloorPLan =
    filterAvailabilitiesBySelectedFloorPlanId(
      availabilitiesFilteredByGuestCountAndTime,
      state.selectedFloorPlanId,
    );

  const filteredSortedAvailabilities =
    filterAvailabilitiesBySelectedFloorPlanListingIds(
      availabilitiesFilteredByGuestCountAndTimeAndFloorPLan,
      selectedFloorPlanListingIds,
    )
      .map(isTrulyBiddable(state.selectedDate, timezone))
      .sort(timeAndPriceSorter);

  const hasUnsupportedGuestCount =
    !availabilitiesFilteredByGuestCount.length && !!availabilities.length;

  const expandedAvailabilityListingIds = uniq(
    availabilitiesFilteredByGuestCountAndTimeAndFloorPLan.map(
      (availability) => availability.listing.id,
    ),
  );

  return {
    expandedAvailabilities:
      availabilitiesFilteredByGuestCountAndTimeAndFloorPLan,
    expandedAvailabilityListingIds,
    filteredAvailabilities: filteredSortedAvailabilities,
    hasUnsupportedGuestCount,
  };
};

export const getSelectedFloorPlan = (
  state: AvailabilityState<Availability, TSelectedAvailability>,
) => state.floorPlans.find((fp) => fp.id === state.selectedFloorPlanId)!;
